import { Injectable } from '@angular/core';
import {
  IFilter,
  IFilterGroup,
  IGenericSelectSearchService,
  IGenericSelectService,
  ODataAndOr,
  ODataOperator,
} from 'src/app/lego-utils';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Material } from 'src/app/models';
import { MaterialSearchDataService } from 'src/app/services';

@Injectable({ providedIn: 'root' })
export class MaterialSearchService
  implements IGenericSelectService<Material>, IGenericSelectSearchService<Material>
{
  public constructor(private readonly materialSearchDataService: MaterialSearchDataService) {}

  public getForSelect(selectedIds$?: Observable<unknown[]>): Observable<Material[]> {
    if (!selectedIds$) {
      return of([]);
    }

    return selectedIds$.pipe(
      switchMap((selectedIds) => {
        const selectedId = selectedIds[0] as string | undefined;

        if (!selectedId) {
          return of([]);
        }

        const filters: IFilter = {
          key: 'MaterialNumber',
          operator: ODataOperator.Equals,
          value: selectedId,
        };

        return this.materialSearchDataService.getMultiple(filters);
      })
    );
  }

  public getForSearch(searchText: string): Observable<Material[]> {
    let filters: IFilter | IFilterGroup;

    if (this.isMaterialNumber(searchText)) {
      // When users search for a specific MaterialNumber we should search outside normal params.
      // The OData service doesn't support OR filters so we have to split it into two filters
      filters = { key: 'MaterialNumber', value: searchText, operator: ODataOperator.Equals };
    } else {
      filters = {
        andOr: ODataAndOr.And,
        filters: [
          {
            key: 'MaterialDescrUpper',
            operator: ODataOperator.Equals,
            // wildcards in the search term isn't correct OData, that would be using contains()
            // it's something the PLM team hacked in and only works on this specific endpoint
            value: `*${searchText.toUpperCase()}*`,
          },
          { key: 'MaterialGroup', value: ['0170', '0177'], operator: ODataOperator.Equals },
          { key: 'MaterialType', value: 'Y940', operator: ODataOperator.Equals },
          { key: 'DevelopmentOwnership', value: 'P', operator: ODataOperator.Equals },
          { key: 'CrossPlantStatus', value: '60', operator: ODataOperator.Equals },
        ],
      };
    }

    return this.materialSearchDataService.getMultiple(filters);
  }

  public getEntityName(): string {
    return 'Material';
  }

  private isMaterialNumber(value: string): boolean {
    return value.length === 8 && !isNaN(parseInt(value, 10));
  }
}
